import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PrimaryButton from '../components/PrimaryButton';
import api from '../api';
import { downloadProjectKickoffCalendar } from '../api';

export const KICKOFF_PROGRAMS = {
    'campus-improvement-grant': {
        title: 'Campus Improvement Grant Project Kickoff Meeting Registration',
        sessions: [
            { key: 'cig-2026-10-12-0930', label: 'Monday, October 12, 2026, 9:30–10:30 a.m. EST', calendarFilename: 'Retool Your School - Campus Improvement Grant Project Kickoff Meeting #1.ics' },
            { key: 'cig-2026-10-12-1330', label: 'Monday, October 12, 2026, 1:30–2:30 p.m. EST', calendarFilename: 'Retool Your School - Campus Improvement Grant Project Kickoff Meeting #2.ics' },
            { key: 'cig-2026-10-12-1600', label: 'Monday, October 12, 2026, 4:00–5:00 p.m. EST', calendarFilename: 'Retool Your School - Campus Improvement Grant Project Kickoff Meeting #3.ics' },
        ],
    },
    'innovation-lounge': {
        title: 'Innovation Lounge Project Kickoff Meeting Registration',
        sessions: [
            { key: 'ilg-2026-10-13-1000', label: 'Tuesday, October 13, 2026, 10:00–11:00 a.m. EST', calendarFilename: 'Retool Your School - Innovation Lounge Grant Project Kickoff Meeting #1.ics' },
            { key: 'ilg-2026-10-13-1400', label: 'Tuesday, October 13, 2026, 2:00–3:00 p.m. EST', calendarFilename: 'Retool Your School - Innovation Lounge Grant Project Kickoff Meeting #2.ics' },
        ],
    },
};

const EMPTY_FORM = { emailAddress: '', firstName: '', lastName: '', schoolName: '', phoneNumber: '', sessionKey: '', website: '' };
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[0-9()+.\-\s]{7,30}$/;

export default function ProjectKickoffRegistration({ programType }) {
    const program = KICKOFF_PROGRAMS[programType];
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState('');
    const [submitted, setSubmitted] = useState(null);
    const [calendarError, setCalendarError] = useState('');
    const [downloadingCalendar, setDownloadingCalendar] = useState(false);
    const selectedSessionKey = program?.sessions.some((session) => session.key === form.sessionKey) ? form.sessionKey : '';

    useEffect(() => {
        setForm(EMPTY_FORM);
        setErrors({});
        setServerError('');
        setSubmitted(null);
        setCalendarError('');
        setDownloadingCalendar(false);
    }, [programType]);

    const validate = (values) => {
        const nextErrors = {};
        ['emailAddress', 'firstName', 'lastName', 'schoolName', 'phoneNumber', 'sessionKey'].forEach((field) => {
            if (!values[field].trim()) nextErrors[field] = 'This field is required.';
        });
        if (values.emailAddress.trim() && !emailPattern.test(values.emailAddress.trim())) nextErrors.emailAddress = 'Enter a valid email address.';
        if (values.phoneNumber.trim() && !phonePattern.test(values.phoneNumber.trim())) nextErrors.phoneNumber = 'Enter a valid phone number.';
        return nextErrors;
    };

    const updateField = (field) => (event) => {
        const value = event.target.value;
        const nextForm = { ...form, [field]: value };
        setForm(nextForm);
        if (errors[field]) setErrors(validate(nextForm));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting) return;
        const nextErrors = validate(form);
        setErrors(nextErrors);
        setServerError('');
        if (Object.keys(nextErrors).length) return;

        const payload = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()]));
        try {
            setSubmitting(true);
            await api.post('/api/public/kickoff-registrations', { ...payload, programType });
            setSubmitted({ programType, sessionKey: payload.sessionKey });
        } catch (error) {
            setServerError(error?.response?.data?.message || 'Unable to submit your registration. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCalendarDownload = async (session) => {
        if (downloadingCalendar || !session?.calendarFilename) return;
        setCalendarError('');
        setDownloadingCalendar(true);
        try {
            const { blob, contentDisposition } = await downloadProjectKickoffCalendar(session.key);
            const filename = filenameFromContentDisposition(contentDisposition) || session.calendarFilename;
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            setCalendarError(error?.message || 'The calendar file is unavailable. Please try again later.');
        } finally {
            setDownloadingCalendar(false);
        }
    };

    if (!program) return null;
    if (submitted) {
        const session = program.sessions.find((item) => item.key === submitted.sessionKey);
        return <Box component="main"><Paper elevation={1} sx={{ maxWidth: 720, mx: 'auto', mt: 4, p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>Registration received</Typography>
            <Typography>Your registration for the {program.title} has been received.</Typography>
            <Typography sx={{ mt: 2, fontWeight: 600 }}>{session?.label}</Typography>
            {calendarError && <Alert severity="error" sx={{ mt: 2 }}>{calendarError}</Alert>}
            {session?.calendarFilename && <Box sx={{ mt: 3 }}><PrimaryButton onClick={() => handleCalendarDownload(session)} disabled={downloadingCalendar} startIcon={<CalendarMonthIcon />}>{downloadingCalendar ? 'Downloading...' : 'Add to Calendar'}</PrimaryButton></Box>}
        </Paper></Box>;
    }

    return <Box component="main"><Paper elevation={1} sx={{ maxWidth: 720, mx: 'auto', mt: 4, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'black', px: 3, py: 2 }}><Box component="img" src="/ryslogo.png" alt="Retool Your School" sx={{ height: 54, display: 'block' }} /></Box>
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 3 }}>{program.title}</Typography>
            {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}
            <TextField fullWidth required label="Email Address" type="email" value={form.emailAddress} onChange={updateField('emailAddress')} error={Boolean(errors.emailAddress)} helperText={errors.emailAddress} sx={{ mb: 2 }} />
            <TextField fullWidth required label="First Name" value={form.firstName} onChange={updateField('firstName')} error={Boolean(errors.firstName)} helperText={errors.firstName} sx={{ mb: 2 }} />
            <TextField fullWidth required label="Last Name" value={form.lastName} onChange={updateField('lastName')} error={Boolean(errors.lastName)} helperText={errors.lastName} sx={{ mb: 2 }} />
            <TextField fullWidth required label="School Name" value={form.schoolName} onChange={updateField('schoolName')} error={Boolean(errors.schoolName)} helperText={errors.schoolName} sx={{ mb: 2 }} />
            <TextField fullWidth required label="Phone Number" type="tel" value={form.phoneNumber} onChange={updateField('phoneNumber')} error={Boolean(errors.phoneNumber)} helperText={errors.phoneNumber} sx={{ mb: 2 }} />
            <FormControl fullWidth required error={Boolean(errors.sessionKey)} sx={{ mb: 1 }}>
                <InputLabel id="session-date-time-label">Session Date and Time</InputLabel>
                <Select labelId="session-date-time-label" label="Session Date and Time" value={selectedSessionKey} onChange={updateField('sessionKey')}>
                    {program.sessions.map((session) => <MenuItem key={session.key} value={session.key}>{session.label}</MenuItem>)}
                </Select>
                {errors.sessionKey && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.sessionKey}</Typography>}
            </FormControl>
            <input aria-hidden="true" autoComplete="off" tabIndex={-1} name="website" value={form.website} onChange={updateField('website')} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }} />
            <Box sx={{ mt: 3 }}><PrimaryButton type="submit" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit registration'}</PrimaryButton></Box>
        </Box>
    </Paper></Box>;
}

export function filenameFromContentDisposition(value) {
    const utf8Match = String(value || '').match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) return decodeURIComponent(utf8Match[1]);
    const filenameMatch = String(value || '').match(/filename="?([^";]+)"?/i);
    return filenameMatch ? filenameMatch[1] : '';
}